import { useCallback, useRef, useState } from "react";
import { Sparkles, Undo2 } from "lucide-react";
import toast from "react-hot-toast";

import { ButtonShadcn as Button } from "@/components/ui/Button";
import { Callout } from "@/components/ui/Callout";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/Popover";
import { Textarea } from "@/components/ui/Textarea";

// Mirrors DRAFT_INSTRUCTION_MAX in server/app/api/v2/outreach/schema/api.py.
export const INSTRUCTION_MAX = 1000;

const hasText = (value) => Boolean((value?.body || "").trim());

/**
 * AI help with the message a recruiter is composing, and the recruiter drives
 * it. With nothing written they say what the message should do; with a draft
 * of their own the AI improves it, taking an optional note on how. The result
 * lands in the composer they are already editing, and Undo puts their own
 * text back. Nothing is sent from here.
 *
 * `value` is the composer's `{ subject, body }`, `onChange` writes one back,
 * and `request(instruction, value)` asks the server — the caller knows which
 * candidate, channel and thread. `reset()` drops a draft still in flight, for
 * a composer that has moved to another conversation.
 */
export const useAiAssist = ({ value, onChange, request }) => {
  const [running, setRunning] = useState(false);
  const [previous, setPrevious] = useState(null);
  const generation = useRef(0);
  // State lags a render behind: two clicks in one frame both saw
  // `running === false` and billed two model calls.
  const inFlight = useRef(false);

  const run = useCallback(
    async (instruction) => {
      if (inFlight.current) return false;
      inFlight.current = true;
      const mine = ++generation.current;
      const before = value;
      setRunning(true);
      try {
        const draft = await request(instruction.trim(), before);
        if (generation.current !== mine) return false;
        onChange({ subject: draft?.subject || before.subject || "", body: draft?.body || "" });
        setPrevious(before);
        return true;
      } catch (err) {
        if (generation.current === mine) toast.error(err?.userMessage || "The AI couldn't answer just now. Try again in a moment.");
        return false;
      } finally {
        if (generation.current === mine) {
          inFlight.current = false;
          setRunning(false);
        }
      }
    },
    [value, onChange, request],
  );

  const undo = useCallback(() => {
    if (previous) onChange(previous);
    setPrevious(null);
  }, [previous, onChange]);

  const reset = useCallback(() => {
    generation.current += 1;
    inFlight.current = false;
    setRunning(false);
    setPrevious(null);
  }, []);

  return { run, running, drafted: previous !== null, undo, dismiss: () => setPrevious(null), reset, improving: hasText(value) };
};

/**
 * The button and its instruction box. "Write with AI" on an empty composer,
 * where the instruction is required; "Improve with AI" over the recruiter's
 * own text, where it is optional. `compact` draws it icon-only, for a chat
 * composer with no room for a label.
 */
export const AiAssistButton = ({ assist, disabled = false, compact = false, className }) => {
  const [open, setOpen] = useState(false);
  const [instruction, setInstruction] = useState("");
  const { improving, running } = assist;
  const label = improving ? "Improve with AI" : "Write with AI";
  const canRun = !running && (improving || instruction.trim().length > 0);

  const submit = async () => {
    if (!canRun) return;
    if (await assist.run(instruction)) {
      setInstruction("");
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={(next) => !running && setOpen(next)}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="tertiary"
          size={compact ? "icon" : "default"}
          disabled={disabled || running}
          aria-label={compact ? label : undefined}
          title={compact ? label : undefined}
          className={className}
        >
          <Sparkles aria-hidden="true" className="text-tone-purple" />
          {!compact && (running ? (improving ? "Improving…" : "Writing…") : label)}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80">
        <form
          aria-label={label}
          onSubmit={(e) => {
            // React bubbles through the portal: without this the composer's
            // own form (Reply) would take the submit and send the email.
            e.preventDefault();
            e.stopPropagation();
            submit();
          }}
          className="flex flex-col gap-2"
        >
          <label htmlFor="ai-assist-instruction" className="text-sm font-medium text-text-primary">
            {improving ? "How should it change?" : "What should it say?"}
            {improving && <span className="font-normal text-text-secondary"> (optional)</span>}
          </label>
          <Textarea
            id="ai-assist-instruction"
            autoFocus
            rows={3}
            maxLength={INSTRUCTION_MAX}
            value={instruction}
            disabled={running}
            onChange={(e) => setInstruction(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                submit();
              }
            }}
            placeholder={improving ? "e.g. shorter, friendlier, mention the relocation budget" : "e.g. invite them to a 15-minute call about the backend role this week"}
            className="resize-none px-3 py-2"
          />
          <p className="text-xs text-text-secondary">
            {improving ? "It fixes and tightens your text and keeps what you wrote." : "You can edit everything it writes before you send."}
          </p>
          <div className="flex items-center justify-end gap-2">
            <Button type="button" variant="tertiary" size="sm" className="rounded-lg" disabled={running} onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" size="sm" className="rounded-lg" disabled={!canRun}>
              {running ? (improving ? "Improving…" : "Writing…") : improving ? "Improve" : "Write"}
            </Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
};

/** Under a composer the AI just filled: read it before sending, or undo. */
export const AiDraftNotice = ({ assist, id, className }) => {
  if (!assist.drafted) return null;
  return (
    <Callout id={id} role="status" tone="info" icon={Sparkles} size="sm" className={className}>
      <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span>
          <strong className="font-semibold text-text-primary">AI draft.</strong> Check it before you send.
        </span>
        <Button type="button" variant="link" size="xs" onClick={assist.undo} className="h-auto p-0 text-tone-purple">
          <Undo2 aria-hidden="true" />
          Undo
        </Button>
      </span>
    </Callout>
  );
};
